import type { CommentModel } from './comments.types';
import type { BaseModel } from './common.types';
import type { FaultInteractionData } from './DashboardBlock';
import type { VisitAmsPartModel } from './visits-ams-part.types';
import type { VisitFaultInteractionsModel } from './visits-fault-interactions.types';

export interface VisitFaultModel extends BaseModel {
  visit: string;
  visitFaultInteractions: VisitFaultInteractionsModel[];
  category: FaultCategory;
  type?: FaultType;
  subType?: FaultSubType;
  customName?: string;
  isCritical: boolean;
  isReported: boolean;
  isResolved: boolean;
  resolvedAt?: string;
  amsPartsUsed: boolean;
  isPendingRevision: boolean;
  // Related Model data
  relatedEntityId?: string;
  relatedEntityName?: string;
  relatedEntityType?: RelatedEntityType;
  visitAmsParts: VisitAmsPartModel[];
  comments: CommentModel[];
  faultInteractionsData: FaultInteractionData[];
}

export type ExtendedVisitFaultModel = VisitFaultModel & {
  visitBarId?: string;
  visitTapId?: string;
  visitCellarId?: string;
  visitRemoteCoolerId?: string;
  visitFobId?: string;
  visitGlassWasherId?: string;
};

export enum RelatedEntityType {
  Bar = 'Bar',
  Tap = 'Tap',
  Cellar = 'Cellar',
  RemoteCooler = 'RemoteCooler',
  Fob = 'Fob',
  GlassWasher = 'GlassWasher',
  Other = 'Other'
}

export enum FaultCategory {
  Technical = 'Technical',
  Operational = 'Operational'
}

export enum OperationalFaultType {
  BackboardHygiene = 'Backboard Hygiene',
  CouplerHygiene = 'Coupler Hygiene',
  GlassWashDetergentRinseaid = 'Glass Wash Detergent / Rinseaid',
  GlassWasherCare = 'Glass Washer Care',
  GlasswareHygiene = 'Glassware Hygiene',
  GlasswareStorage = 'Glassware Storage',
  NozzleHygiene = 'Nozzle Hygiene',
  ObstructionBlockedAirflow = 'Obstruction / Blocked Airflow',
  OtherHygiene = 'Other Hygiene',
  PourTechnique = 'Pour Technique',
  StockControl = 'Stock Control',
  GlassWasherChemicals = 'Glass Washer Chemicals',
  GlassWasherMaintenance = 'Glass Washer Maintenance',
  CellarManagement = 'Cellar Management',
  KegChange = 'Keg Change',
  FobControl = 'FOB Control',
  CaskProcess = 'Cask Process',
  RiskAssessment = 'Risk Assessment'
}

export enum OtherFaultType {
  Other = 'Other'
}

export enum TechnicalFaultVisitBarType {
  ExposedLines = 'Exposed Lines',
  DamagedInsulation = 'Damaged Insulation',
  ContaminatedInsulation = 'Contaminated Insulation',
  ExposedFittings = 'Exposed Fittings',
  ExcessiveRun = 'Excessive Run'
}

export enum TechnicalFaultVisitTapType {
  ProductLine = 'Product Line',
  FlowController = 'Flow Controller',
  FontTBar = 'Font / T-bar',
  TapBody = 'Tap Body',
  Nozzle = 'Nozzle'
}

export enum TechnicalFaultVisitTapSubTypeProductLine {
  OtherTaint = 'Other Taint',
  ChlorophenolTaint = 'Chlorophenol Taint',
  WaterTaint = 'Water Taint',
  OxidationTaint = 'Oxidation Taint',
  SpoilageTaint = 'Spoilage Taint'
}

export enum TechnicalFaultVisitTapSubTypeFlowController {
  Leaking = 'Leaking',
  Blocked = 'Blocked',
  FlowIssue = 'Flow Issue',
  Jammed = 'Jammed'
}

export enum TechnicalFaultVisitTapSubTypeFontTBar {
  MountingFault = 'Mounting Fault',
  Leaking = 'Leaking',
  WearTear = 'Wear & Tear',
  Branding = 'Branding'
}

export enum TechnicalFaultVisitTapSubTypeTapBody {
  DamagedGaskets = 'Damaged Gaskets',
  CompensatorFault = 'Compensator Fault',
  MountingNutSlack = 'Mounting Nut Slack',
  WornMountingThreads = 'Worn Mounting Threads',
  DisplacedMissingHingePin = 'Displaced / Missing Hinge Pin',
  HandleOperation = 'Handle Operation',
  TapSealTypeIncorrect = 'Tap Seal Type Incorrect',
  TapSealDamaged = 'Tap Seal Damaged',
  Leaking = 'Leaking',
  Blocked = 'Blocked'
}

export enum TechnicalFaultVisitTapSubTypeNozzle {
  DamagedWorn = 'Damaged / Worn',
  PlasticDelamination = 'Plastic Delamination',
  Missing = 'Missing',
  CreamerPartsMissingDamaged = 'Creamer Parts Missing / Damaged'
}

export enum TechnicalFaultVisitGlassWasherType {
  Corrosion = 'Corrosion',
  MissingPanels = 'Missing Panels',
  ChemicalDosingPumps = 'Chemical Dosing Pumps',
  FiltersDamagedMissing = 'Filters Damaged / Missing',
  SprayArmsDamagedMissing = 'Spray Arms Damaged / Missing',
  DrainageFailure = 'Drainage Failure',
  TemperatureFailure = 'Temperature Failure',
  WaterPumpFailure = 'Water Pump Failure',
  PowerFailure = 'Power Failure'
}

export enum TechnicalFaultVisitCellarType {
  GasCylinderSupply = 'Gas Cylinder / Supply',
  PrimaryGasRegulator = 'Primary Gas Regulator',
  GasBlender = 'Gas Blender',
  Compressor = 'Compressor',
  GasRingmain = 'Gas Ringmain',
  TemperatureProofing = 'Temperature Proofing',
  CleaningBell = 'Cleaning Bell',
  CleaningPump = 'Cleaning Pump',
  CleaningRingmain = 'Cleaning Ringmain',
  CellarCooler = 'Cellar Cooler'
}

export enum TechnicalFaultVisitCellarSubTypeGasCylinderSupply {
  DamagedFaulty = 'Damaged / Faulty',
  ExcessiveDischargeFrosting = 'Excessive Discharge/ Frosting'
}

export enum TechnicalFaultVisitCellarSubTypePrimaryGasRegulator {
  RegulationFailure = 'Regulation Failure',
  CylinderHoseDamaged = 'Cylinder Hose Damaged',
  SealMissingDamaged = 'Seal Missing / Damaged',
  FittingsDamagedLeaking = 'Fittings Damaged / Leaking',
  PressureReliefValveFailure = 'Pressure Relief Valve Failure',
  FreezingOver = 'Freezing Over',
  PressureLimitIncorrect = 'Pressure Limit Incorrect'
}

export enum TechnicalFaultVisitCellarSubTypeGasBlender {
  PowerFailure = 'Power Failure',
  GasLeak = 'Gas Leak',
  MaxOutputPressureLowHigh = 'Max Output Pressure Low / High',
  N2Production = 'N2 Production',
  N2Storage = 'N2 Storage',
  UnitTurnedOff = 'Unit Turned Off',
  GeneralFault = 'General Fault'
}

export enum TechnicalFaultVisitCellarSubTypeCompressor {
  Overloading = 'Overloading',
  MountingDamaged = 'Mounting Damaged',
  PowerFailure = 'Power Failure',
  Leaking = 'Leaking',
  PressureRegulatorFailure = 'Pressure Regulator Failure',
  OutputPressureLowHigh = 'Output Pressure Low / High',
  PressureReliefValveFailure = 'Pressure Relief Valve Failure'
}

export enum TechnicalFaultVisitCellarSubTypeGasRingmain {
  RingmainLoopIncomplete = 'Ringmain Loop Incomplete',
  MaxPressureLowHigh = 'Max Pressure Low / High',
  Leaking = 'Leaking',
  LineDamaged = 'Line Damaged',
  ExhaustNotVented = 'Exhaust Not Vented',
  ReliefValveMissing = 'Relief Valve Missing',
  Overloading = 'Overloading'
}

export enum TechnicalFaultVisitCellarSubTypeTemperatureProofing {
  ExcessiveHeatSources = 'Excessive Heat Sources',
  DoorInsulationDamagedMissing = 'Door Insulation Damaged / Missing',
  DoorDamaged = 'Door Damaged ',
  DoorFailsToClose = 'Door Fails To Close',
  KegHatchExposed = 'Keg Hatch Exposed',
  CeilingHeightExcessive = 'Ceiling Height Excessive',
  CeilingIncomplete = 'Ceiling Incomplete',
  CellarWallInsulationInsufficient = 'Cellar Wall Insulation Insufficient'
}

export enum TechnicalFaultVisitCellarSubTypeCleaningBell {
  Damaged = 'Damaged',
  ExcessiveStaining = 'Excessive Staining',
  Leaking = 'Leaking',
  Missing = 'Missing'
}

export enum TechnicalFaultVisitCellarSubTypeCleaningPump {
  GeneralFault = 'General Fault',
  DiaphragmLeaking = 'Diaphragm Leaking',
  GasPressureIncorrect = 'Gas Pressure Incorrect',
  FittingsDamagedLeaking = 'Fittings Damaged / Leaking',
  GasExhaustNotVented = 'Gas Exhaust Not Vented',
  GasExhaustSaturated = 'Gas Exhaust Saturated',
  PumpMissing = 'Pump Missing'
}

export enum TechnicalFaultVisitCellarSubTypeCleaningRingmain {
  LineDamagedLeaking = 'Line Damaged / Leaking',
  ExcessiveStaining = 'Excessive Staining',
  MineralDeposits = 'Mineral Deposits',
  LineBalancingInconsistent = 'Line Balancing Inconsistent',
  LineGaugeInconsistent = 'Line Gauge Inconsistent',
  AirIngress = 'Air Ingress',
  Overloading = 'Overloading',
  MissingUndersized = 'Missing / Undersized'
}

export enum TechnicalFaultVisitCellarSubTypeCellarCooler {
  ThirdPartyManagementSystem = '3rd Party Management System',
  FanDamaged = 'Fan Damaged',
  HeatDumpDamaged = 'Heat Dump Damaged',
  RadiatorDamaged = 'Radiator Damaged',
  TemperatureFailure = 'Temperature Failure',
  SystemTurnedOff = 'System Turned Off',
  SystemUndersized = 'System Undersized'
}

export enum TechnicalFaultVisitFobType {
  CleaningSocket = 'Cleaning Socket',
  KegProduct = 'Keg / Product',
  Coupler = 'Coupler',
  DropLine = 'Drop Line',
  BeerPump = 'Beer Pump',
  SecondaryGasRegulator = 'Secondary Gas Regulator',
  FobChamber = 'FOB Chamber',
  FeedLine = 'Feed Line'
}

export enum TechnicalFaultVisitFobSubTypeCleaningSocket {
  Corrosion = 'Corrosion',
  SealDamagedPerished = 'Seal Damaged / Perished',
  ConnectionDamaged = 'Connection Damaged',
  IncorrectType = 'Incorrect Type',
  Leaking = 'Leaking',
  ChemicalSoiling = 'Chemical Soiling'
}

export enum TechnicalFaultVisitFobSubTypeKegProduct {
  ConnectionSurfaceDamaged = 'Connection Surface Damaged',
  IncorrectGas = 'Incorrect Gas',
  ProductSpoilage = 'Product Spoilage',
  SealDamagedPerished = 'Seal Damaged / Perished'
}

export enum TechnicalFaultVisitFobSubTypeCoupler {
  NonReturnValveDamagedMissing = 'Non Return Valve Damaged / Missing',
  SealsDamagedMissing = 'Seals Damaged / Missing',
  FittingsDamaged = 'Fittings Damaged',
  ConnectionSurfaceDamaged = 'Connection Surface Damaged'
}

export enum TechnicalFaultVisitFobSubTypeDropLine {
  BeerLineDamaged = 'Beer Line Damaged',
  GasLineDamaged = 'Gas Line Damaged',
  FittingWearTear = 'Fitting Wear & Tear',
  LengthExcessiveShort = 'Length Excessive / Short'
}

export enum TechnicalFaultVisitFobSubTypeBeerPump {
  GeneralFault = 'General Fault',
  DiaphragmLeaking = 'Diaphragm Leaking',
  TriggeringExcessively = 'Triggering Excessively',
  GasPressureIncorrect = 'Gas Pressure Incorrect',
  FittingsDamagedLeaking = 'Fittings Damaged / Leaking',
  GasExhaustNotVented = 'Gas Exhaust Not Vented',
  GasExhaustSaturated = 'Gas Exhaust Saturated'
}

export enum TechnicalFaultVisitFobSubTypeSecondaryGasRegulator {
  GasEquilibriumPressureOutOfRange = 'Gas Equilibrium Pressure Out of Range',
  GasPumpPressureIncorrect = 'Gas Pump Pressure Incorrect',
  Leaking = 'Leaking',
  PressureCreep = 'Pressure Creep'
}

export enum TechnicalFaultVisitFobSubTypeChamber {
  WallsDamagedCracked = 'Walls Damaged / Cracked',
  MountingDamaged = 'Mounting Damaged',
  FloatDamaged = 'Float Damaged',
  FloatReleaseDamaged = 'Float Release Damaged',
  FittingsDamagedLeaking = 'Fittings Damaged / Leaking',
  Staining = 'Staining',
  ExhaustValveDamagedLeaking = 'Exhaust Valve Damaged / Leaking',
  SealsDamagedPerished = 'Seals Damaged / Perished',
  ProductLevelDropping = 'Product Level Dropping'
}

export enum TechnicalFaultVisitFobSubTypeFeedLine {
  LineDamaged = 'Line Damaged',
  InsulationDamagedMissing = 'Insulation Damaged / Missing',
  ExcessiveFittings = 'Excessive Fittings',
  FittingLeaking = 'Fitting Leaking'
}

export enum TechnicalFaultVisitRemoteCoolerType {
  General = 'General',
  WaterBath = 'Water Bath',
  TopPump = 'Top Pump',
  ProductCoil = 'Product Coil',
  HeatDumpSplit = 'Heat Dump (Split)',
  IntegralRadiatorGrilleDamaged = 'Integral Radiator - Grille Damaged',
  GlycolReservoirGlycolLevelLow = 'Glycol Reservoir - Glycol Level Low',
  FeedPython = 'Feed Python'
}

export enum TechnicalFaultVisitRemoteCoolerSubTypeGeneral {
  GeneralFault = 'General Fault',
  IntegralUnitVentilation = 'Integral Unit Ventilation',
  ExcessiveLoading = 'Excessive Loading'
}

export enum TechnicalFaultVisitRemoteCoolerSubTypeWaterBath {
  Contamination = 'Contamination',
  TemperatureHigh = 'Temperature High',
  IceBankAbsent = 'Ice Bank Absent',
  IceBankExcessive = 'Ice Bank Excessive',
  WaterLevelLow = 'Water Level Low'
}

export enum TechnicalFaultVisitRemoteCoolerSubTypeTopPump {
  GeneralFailure = 'General Failure',
  PowerSupplyDamaged = 'Power Supply Damaged',
  PumpSeizedInterference = 'Pump Seized / Interference',
  PumpOverheating = 'Pump Overheating',
  RecirculationRateIncorrect = 'Recirculation Rate Incorrect'
}

export enum TechnicalFaultVisitRemoteCoolerSubTypeProductCoil {
  DamagedRestriction = 'Damaged / Restriction',
  ConnectorDamagedLeaking = 'Connector Damaged / Leaking'
}

export enum TechnicalFaultVisitRemoteCoolerSubTypeHeatDumpSplit {
  GeneralFailure = 'General Failure',
  NoHeatTransfer = 'No Heat Transfer',
  FanDamaged = 'Fan Damaged',
  RadiatorDamaged = 'Radiator Damaged',
  MountingDamaged = 'Mounting Damaged'
}

export enum TechnicalFaultVisitRemoteCoolerSubTypeFeedPython {
  InsulationMissing = 'Insulation Missing',
  NotBackCooledAmbientInstall = 'Not Back Cooled (ambient install)'
}

export type FaultType =
  | OperationalFaultType
  | TechnicalFaultVisitBarType
  | TechnicalFaultVisitTapType
  | TechnicalFaultVisitGlassWasherType
  | TechnicalFaultVisitCellarType
  | TechnicalFaultVisitFobType
  | TechnicalFaultVisitRemoteCoolerType
  | OtherFaultType;

export type FaultSubType =
  | TechnicalFaultVisitTapSubTypeProductLine
  | TechnicalFaultVisitTapSubTypeFlowController
  | TechnicalFaultVisitTapSubTypeFontTBar
  | TechnicalFaultVisitTapSubTypeTapBody
  | TechnicalFaultVisitTapSubTypeNozzle
  | TechnicalFaultVisitCellarSubTypeGasCylinderSupply
  | TechnicalFaultVisitCellarSubTypePrimaryGasRegulator
  | TechnicalFaultVisitCellarSubTypeGasBlender
  | TechnicalFaultVisitCellarSubTypeCompressor
  | TechnicalFaultVisitCellarSubTypeGasRingmain
  | TechnicalFaultVisitCellarSubTypeTemperatureProofing
  | TechnicalFaultVisitCellarSubTypeCleaningBell
  | TechnicalFaultVisitCellarSubTypeCleaningPump
  | TechnicalFaultVisitCellarSubTypeCleaningRingmain
  | TechnicalFaultVisitCellarSubTypeCellarCooler
  | TechnicalFaultVisitRemoteCoolerSubTypeGeneral
  | TechnicalFaultVisitRemoteCoolerSubTypeWaterBath
  | TechnicalFaultVisitRemoteCoolerSubTypeTopPump
  | TechnicalFaultVisitRemoteCoolerSubTypeProductCoil
  | TechnicalFaultVisitRemoteCoolerSubTypeHeatDumpSplit
  | TechnicalFaultVisitRemoteCoolerSubTypeFeedPython
  | TechnicalFaultVisitFobSubTypeCleaningSocket
  | TechnicalFaultVisitFobSubTypeKegProduct
  | TechnicalFaultVisitFobSubTypeCoupler
  | TechnicalFaultVisitFobSubTypeDropLine
  | TechnicalFaultVisitFobSubTypeBeerPump
  | TechnicalFaultVisitFobSubTypeSecondaryGasRegulator
  | TechnicalFaultVisitFobSubTypeChamber
  | TechnicalFaultVisitFobSubTypeFeedLine;
